import { useMemo } from 'react';

import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { MY_PERFORMANCE_PATH, filterFiltersByUrl } from '@/modules/analytics/constants';
import { selectFilters } from '@/modules/analytics/redux/selectors';
import { selectAccountId } from '@/redux/session/selectors';
import { selectUserId } from '@/redux/user/selectors';
import { getAnalyticsRangeUTC } from '@/util/analytics';
import { getGraphQLSet } from '@/util/util';

import { ratingsFilterToSet } from '../utils/expandRatings';

export interface HumanAgentsQueryVariables {
  start_time: string | null;
  end_time: string | null;
  // _uuid / _text / _int4 SQL-function args: Hasura expects a Postgres array
  // literal string ("{a,b}"), not a JSON array. Always serialize via getGraphQLSet.
  desk_ids?: string | null;
  channels?: string | null;
  channel_types?: string | null;
  agent_ids?: string | null;
  csat_human_agent?: string | null;
  min_num_user_messages?: number | null;
  is_test?: boolean;
  user_rating?: string | null;
}

export const useHumanAgentsQueryVariables = (dateOverride?: {
  startDate: string;
  endDate: string;
}) => {
  const accountId = useSelector(selectAccountId);
  const userId = useSelector(selectUserId);
  const filters = useSelector(selectFilters);
  const location = useLocation();
  const isMyPerformance = location.pathname.includes(`/${MY_PERFORMANCE_PATH}`);

  const effectiveStartDate = dateOverride?.startDate ?? filters.startDate;
  const effectiveEndDate = dateOverride?.endDate ?? filters.endDate;

  const variables = useMemo<HumanAgentsQueryVariables>(() => {
    const range = getAnalyticsRangeUTC(effectiveStartDate, effectiveEndDate);
    const vars: HumanAgentsQueryVariables = {
      start_time: range.startDate,
      end_time: range.endDate,
    };

    const allowed = filters.analytics.filter((f) => filterFiltersByUrl(f.type, location.pathname));

    for (const filter of allowed) {
      if (filter.type === 'deskIds') {
        vars.desk_ids = getGraphQLSet(filter.deskIds?.map((o) => o.value) ?? []);
      } else if (filter.type === 'agentIds') {
        vars.agent_ids = getGraphQLSet(filter.agentIds?.map((o) => o.value) ?? []);
      } else if (filter.type === 'channels') {
        vars.channels = getGraphQLSet(filter.channels ?? []);
      } else if (filter.type === 'isTest') {
        vars.is_test = filter.isTest;
      } else if (filter.type === 'minNumUserMessages') {
        // Picker stores the threshold as a string ('2', '3', …) but the GQL
        // variable is declared as Int, so coerce explicitly.
        const raw = filter.minNumUserMessages;
        const parsed = raw == null ? null : Number(raw);
        vars.min_num_user_messages = parsed != null && !Number.isNaN(parsed) ? parsed : null;
      } else if (filter.type === 'ratings') {
        vars.user_rating = ratingsFilterToSet(filter.ratings, filter.ratingsMode);
      } else if (filter.type === 'csatBrain') {
        vars.csat_human_agent = ratingsFilterToSet(filter.csatBrain, filter.csatBrainMode);
      }
    }

    // Lock to the signed-in agent after the filter loop so a stale URL filter cannot widen it.
    if (isMyPerformance && userId) {
      vars.agent_ids = getGraphQLSet([userId]);
    }

    return vars;
  }, [
    filters.analytics,
    effectiveEndDate,
    effectiveStartDate,
    location.pathname,
    isMyPerformance,
    userId,
  ]);

  const skip = !accountId || !filters.filtersLoaded || (isMyPerformance && !userId);

  return { variables, skip };
};
